import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { isReachable, onReachabilityChange, reportWentOffline } from './reachability';
import { useQueryClient } from '@tanstack/react-query';
import { failedOf, pendingOf, type OutboxEntry } from '@/domain';
import { useOutbox } from '../container';
import { keys } from '../query-keys';
import type { OutboxSyncDriver } from '../ports/OutboxQueue';

export type { OutboxSyncDriver };

/**
 * The outbox, as the shell sees it. Section 9 rules 3 and 4.
 *
 * `useSyncExternalStore` rather than a query, because the outbox is local state
 * that changes on every command; polling a local database through the server
 * cache would be a layer of indirection for nothing.
 */

export function useOutboxEntries(): OutboxEntry[] {
  const outbox = useOutbox();
  const [entries, setEntries] = useState<OutboxEntry[]>([]);

  // The subscription is the trigger; the read is asynchronous because the
  // store is, so the value is held in state rather than returned from a getter.
  const reload = useCallback(() => {
    void outbox.all().then(setEntries);
  }, [outbox]);

  useEffect(() => {
    reload();
    return outbox.subscribe(reload);
  }, [outbox, reload]);

  return entries;
}

export interface OutboxStatus {
  pending: number;
  failed: number;
  failedEntries: OutboxEntry[];
}

export function useOutboxStatus(): OutboxStatus {
  const entries = useOutboxEntries();
  return useMemo(() => {
    const failedEntries = failedOf(entries);
    return {
      pending: pendingOf(entries).length,
      failed: failedEntries.length,
      failedEntries,
    };
  }, [entries]);
}

/**
 * Whether the browser believes it has a network.
 *
 * `navigator.onLine` is famously optimistic: it reports true for a phone
 * associated with an access point that has no route to anywhere. It is still
 * worth reading, because when it says false it is always right, and false is
 * the case the strip exists for. A send that fails anyway lands in the outbox,
 * which is the real safety net.
 */
export function useIsOnline(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const offline = () => {
        reportWentOffline();
        onChange();
      };
      window.addEventListener('online', onChange);
      window.addEventListener('offline', offline);
      // The server answering is a connection too: see application/outbox/reachability.
      const stop = onReachabilityChange(onChange);
      return () => {
        window.removeEventListener('online', onChange);
        window.removeEventListener('offline', offline);
        stop();
      };
    },
    () => isReachable(navigator.onLine),
    // Server rendered or pre-hydration: assume a network rather than flashing
    // the offline strip at every user on every cold load.
    () => true,
  );
}

/** A replay pass every this often, as a floor under reconnect and focus. */
const SWEEP_MS = 30_000;

/**
 * Drives the replay: on reconnect, on app focus, and on a slow interval.
 *
 * Three triggers rather than one because each covers a case the others miss. A
 * phone carried back into range fires `online`. A phone whose screen was off
 * fires `visibilitychange` and may never fire `online` at all. The interval
 * covers the access point that never dropped the association but did lose its
 * route, which is the common failure in a building with a furnace in it.
 *
 * Mounted once, in `App`, for the same reason as the ticker in section 10.
 */
export function useOutboxReplay(driver: OutboxSyncDriver | null): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (driver === null) return;

    let live = true;

    const sweep = () => {
      void driver.run().then((result) => {
        // Only invalidate when something actually landed, or a queue that is
        // permanently stuck would refetch the whole app every thirty seconds.
        if (!live || result.sent === 0) return;
        void queryClient.invalidateQueries({ queryKey: keys.kits.all });
        void queryClient.invalidateQueries({ queryKey: keys.board() });
      });
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') sweep();
    };

    window.addEventListener('online', sweep);
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(sweep, SWEEP_MS);
    sweep();

    return () => {
      live = false;
      window.removeEventListener('online', sweep);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [driver, queryClient]);
}
